// TIPSY TEACUP (§6.2): a porcelain saucer that tilts toward the crowd, the spinning cup at its
// center, four sugar cubes and the Sudden Death rim chips.
//  - TILT (replicated `tiltX/tiltY`): a first-order filter (τ 0.6 s) toward the mass-weighted
//    centroid of the grounded beans, clamped to 12° (15° from Ramp); grounded bodies slide toward
//    the low side at up to 2.5 m/s² (3.2 from Ramp). Runs in predict mode too (§18.2).
//  - CUP (replicated `cupOccupant`): a bean that drops into the cup is SPUN for 90 ticks riding the
//    rim at the cup angle θc = 2π·beat/8, then flung at 9 m/s (vz 3) along its input (8-way) or
//    θc; one occupant at a time, a 180-tick re-entry cooldown, other landers bounce off the rim.
//  - RIM CHIPS (replicated `wedgeOrder`, `wedgeWarn`, `wedgeFallen`, `sdCursor`): from Sudden Death,
//    the rim annulus loses one 45° wedge every 2 SD bars, each warned 1 bar ahead, in the order low
//    side, +1, −1, +2, −2 … fixed at SD start.
// Every schedule is a function of the round clock; the cup and the wedges also run for the local
// bean in predict mode, so landing in the cup and losing your floor are predicted exactly.
import { NO_SLOT, TICK_HZ, TUNE, VOID } from '../constants';
import { EV, HazardKind, emit } from '../events';
import { BS, Cause, EK, MapId, RoundPhase } from '../ids';
import { DEG, TAU } from '../math';
import { randInt } from '../rng';
import { spawnEntity } from '../sim/entities';
import { isGrounded } from '../sim/edges';
import { isPredicting, isPuppet, refreshTouch } from '../sim/world';
import type { Bean, Vec2, World } from '../types';
import { MAP_TEMPO, beatAtTick, createTempo, roundTempo, type Tempo } from './clock';
import { addTelegraph, circleSolid, copySolids, nearSolid, neutralHazardPose, progress, ringPoints, seededStream } from './common';
import { inSectoredDisc, nearestOnSectoredDisc, sectorIndex, type SectoredDisc } from './geometry';
import { dropFromVanishedGround, emitFire, emitWarn } from './hazards';
import { SolidTag, type HazardPose, type MapDef } from './mapTypes';
import { datan2, dcos, dsin } from '../sim/dmath';

export const TUNING = {
  saucerRadius: 7.5,
  spawnRadius: 5.0,
  spawnStartDeg: 22.5,
  tilt: {
    /** Max tilt (degrees) in Calm and from Ramp on. */
    maxDegCalm: 12, maxDegRamp: 15,
    /** Filter time constant in seconds: tilt += (target − tilt)·(dt / tau). */
    tauSec: 0.6,
    /** Slide acceleration toward the low side at full tilt (m/s²), Calm and from Ramp on. */
    slideCalm: 2.5, slideRamp: 3.2,
  },
  cup: {
    radius: 1.3, top: 0.8,
    /** Capture: airborne, descending, center within `captureRadius` of the origin, z ≤ `captureZ`. */
    captureRadius: 1.0, captureZ: 0.8,
    spunTicks: 90, orbitRadius: 0.5,
    /** The cup turns once per this many beats (θc = 2π·beat/8). */
    revBeats: 8,
    /** SPUN beans ride at the rim height, so the fling clears the wall in any direction. */
    rideZ: 0.8,
    flingSpeed: 9, flingVz: 3, cooldownTicks: 180,
    /** A second lander bounces off the rim: radial reflect, vz 2, at least this outward speed (clears the wall). */
    bounceVz: 2, bounceMinOut: 4,
  },
  cubes: { radius: 3.2, startDeg: 45, count: 4 },
  rim: {
    /** The annulus inner..outer splits into `count` wedges centered at k·45°. */
    inner: 4.5, count: 8,
    /** One wedge falls every `everyBars` SD bars, each warned `warnBars` before. */
    everyBars: 2, warnBars: 1,
    /** Radius of the wedge's event / telegraph point. */
    markRadius: 6,
    /** Crates stop landing on the rim this many bars before Sudden Death. */
    crateLeadBars: 2,
  },
  /** Bot danger on a doomed wedge: from `wedgeMin` once it is in view, rising to 1 as it falls. */
  danger: { wedgeMin: 0.45 },
} as const;

export const CUBE_HOMES: readonly Vec2[] = ringPoints(TUNING.cubes.radius, TUNING.cubes.startDeg, TUNING.cubes.count);

const R = TUNING.saucerRadius;
const CUP = TUNING.cup;
const RIM = TUNING.rim;
const WEDGE_STEP = TAU / RIM.count;
const SOLIDS = [circleSolid(0, 0, CUP.radius, CUP.top, TUNE.body.restitution, SolidTag.CUP, 0)];
/** The saucer: an always-solid disc of radius 4.5 plus eight rim wedges out to 7.5. */
export const SAUCER: SectoredDisc = { inner: RIM.inner, outer: R, count: RIM.count };

const tempo = createTempo();

export function teacupTempo(w: World): Tempo {
  return roundTempo(w.cfg.mapId, w.cfg.modeId, w.cfg.capTickOverride, tempo);
}

function rampOn(w: World): boolean {
  return w.phase === RoundPhase.RAMP || w.phase === RoundPhase.SUDDEN_DEATH || w.phase === RoundPhase.OVER;
}

/** Max tilt in radians for the world's current phase. */
export function maxTilt(w: World): number {
  return (rampOn(w) ? TUNING.tilt.maxDegRamp : TUNING.tilt.maxDegCalm) * DEG;
}

// ---- Tilt ---------------------------------------------------------------------------------------

/** Beans that weigh on the saucer: standing on it (a held bean weighs on its carrier). */
function weighs(w: World, b: Bean): boolean {
  if (!b.active || b.out) return false;
  switch (b.state) {
    case BS.LEDGE: case BS.CLIMB: case BS.SPUN: case BS.FALLING: case BS.GHOST: case BS.RESPAWN: case BS.FLOAT:
      return false;
    case BS.GRABBED: {
      const c = w.beans[b.partner];
      return c !== undefined && isGrounded(c);
    }
    default:
      return isGrounded(b);
  }
}

/** One tick of the tilt filter toward the beans' mass-weighted centroid (§6.2). */
export function stepTilt(w: World): void {
  let mx = 0;
  let my = 0;
  let m = 0;
  for (const b of w.beans) {
    if (!weighs(w, b)) continue;
    mx += b.pos.x * b.mass;
    my += b.pos.y * b.mass;
    m += b.mass;
  }
  const max = maxTilt(w);
  let tx = 0;
  let ty = 0;
  if (m > 0) {
    tx = (mx / m / R) * max;
    ty = (my / m / R) * max;
    const t = Math.hypot(tx, ty);
    if (t > max) {
      tx *= max / t;
      ty *= max / t;
    }
  }
  const k = 1 / (TUNING.tilt.tauSec * TICK_HZ);
  w.map.tiltX += (tx - w.map.tiltX) * k;
  w.map.tiltY += (ty - w.map.tiltY) * k;
}

// ---- Cup ----------------------------------------------------------------------------------------

/** The cup angle θc = 2π·beat/8 at a (fractional) round tick. */
export function cupAngle(tick: number, t: Tempo): number {
  return (TAU * beatAtTick(t, tick)) / CUP.revBeats;
}

/** Angular speed of the cup in rad/s at `tick`. */
function cupOmega(tick: number, t: Tempo): number {
  const tpb = tick < t.sdStart ? t.tpb : t.tpbSD;
  return (TAU / CUP.revBeats / tpb) * TICK_HZ;
}

/** States that can drop into the cup (or bounce off its rim). */
function canLand(b: Bean): boolean {
  switch (b.state) {
    case BS.NORMAL: case BS.LUNGE: case BS.TUMBLE: case BS.SLIP: case BS.GRAB_START: case BS.GRAB_MISS:
    case BS.PUNCH_WINDUP: case BS.PUNCH:
      return true;
    default:
      return false;
  }
}

function ride(b: Bean, tick: number, t: Tempo): void {
  const a = cupAngle(tick, t);
  const v = cupOmega(tick, t) * CUP.orbitRadius;
  b.pos.x = dcos(a) * CUP.orbitRadius;
  b.pos.y = dsin(a) * CUP.orbitRadius;
  b.vel.x = -dsin(a) * v;
  b.vel.y = dcos(a) * v;
  b.z = CUP.rideZ;
  b.vz = 0;
  b.facing = a + Math.PI / 2;
  // The fling reads this tick's stick (8-way): clear the axis signs so they hold only the current input.
  b.prev.mxSign = 0;
  b.prev.mySign = 0;
}

function capture(w: World, b: Bean, t: Tempo): void {
  b.state = BS.SPUN;
  b.st = 0;
  b.groundZ = 0;
  w.map.cupOccupant = b.slot;
  ride(b, w.tick, t);
  emit(w, EV.SPUN, b.slot, NO_SLOT, 0, 0, b.pos.x, b.pos.y);
}

function fling(w: World, b: Bean, t: Tempo): void {
  let dx = b.prev.mxSign;
  let dy = b.prev.mySign;
  if (dx === 0 && dy === 0) {
    const a = cupAngle(w.tick, t);
    dx = dcos(a);
    dy = dsin(a);
  }
  const l = Math.hypot(dx, dy);
  b.state = BS.TUMBLE;
  b.st = 0;
  b.vel.x = (dx / l) * CUP.flingSpeed;
  b.vel.y = (dy / l) * CUP.flingSpeed;
  b.z = CUP.rideZ;
  b.vz = CUP.flingVz;
  b.groundZ = 0;
  b.facing = datan2(dy, dx);
  b.cdCup = CUP.cooldownTicks;
  w.map.cupOccupant = NO_SLOT;
  if (!isPredicting(w)) refreshTouch(w, b, Cause.CUP);
  emit(w, EV.FLUNG, b.slot, NO_SLOT, 0, 0, b.pos.x, b.pos.y);
}

/** Radial reflect off the rim with at least `bounceMinOut` outward, and a little hop. */
function bounce(b: Bean): void {
  const d = Math.hypot(b.pos.x, b.pos.y);
  const rx = d > 1e-6 ? b.pos.x / d : dcos(b.facing);
  const ry = d > 1e-6 ? b.pos.y / d : dsin(b.facing);
  let vr = b.vel.x * rx + b.vel.y * ry;
  if (vr < 0) {
    b.vel.x -= 2 * vr * rx;
    b.vel.y -= 2 * vr * ry;
    vr = -vr;
  }
  if (vr < CUP.bounceMinOut) {
    b.vel.x += (CUP.bounceMinOut - vr) * rx;
    b.vel.y += (CUP.bounceMinOut - vr) * ry;
  }
  b.vz = CUP.bounceVz;
}

function stepCup(w: World, t: Tempo): void {
  const occ = w.map.cupOccupant;
  if (occ !== NO_SLOT) {
    const b = w.beans[occ];
    if (!b || !b.active || b.out || b.state !== BS.SPUN) w.map.cupOccupant = NO_SLOT;
    else if (!isPuppet(w, occ)) {
      if (b.st >= CUP.spunTicks) fling(w, b, t);
      else ride(b, w.tick, t);
    }
  }
  if (w.tick < 0) return;
  const r2 = CUP.captureRadius * CUP.captureRadius;
  for (const b of w.beans) {
    if (!b.active || b.out || isPuppet(w, b.slot) || !canLand(b)) continue;
    if (b.vz >= 0 || isGrounded(b) || b.z > CUP.captureZ) continue;
    if (b.pos.x * b.pos.x + b.pos.y * b.pos.y > r2) continue;
    if (w.map.cupOccupant === NO_SLOT && b.cdCup === 0) capture(w, b, t);
    else bounce(b);
  }
}

// ---- Rim chips ----------------------------------------------------------------------------------

/** Ticks in one SD bar. */
function sdBarTicks(t: Tempo): number {
  return TUNE.clock.beatsPerBar * t.tpbSD;
}

/** Tick at which the `i`-th wedge of the order starts warning. */
export function wedgeWarnTick(i: number, t: Tempo): number {
  return t.sdStart + i * RIM.everyBars * sdBarTicks(t);
}

/** Tick at which the `i`-th wedge of the order falls. */
export function wedgeFallTick(i: number, t: Tempo): number {
  return wedgeWarnTick(i, t) + RIM.warnBars * sdBarTicks(t);
}

/** The SD wedge order starting from wedge `low`: low, +1, −1, +2, −2, +3, −3, +4. */
export function wedgeOrderFrom(low: number, out: number[] = new Array<number>(RIM.count)): number[] {
  const n = RIM.count;
  out[0] = low;
  for (let i = 1; i < n; i++) {
    const step = Math.ceil(i / 2);
    out[i] = (((low + (i % 2 === 1 ? step : -step)) % n) + n) % n;
  }
  return out;
}

/** The low side's wedge (the tilt direction), or a seeded wedge when the saucer is level. */
export function lowWedge(w: World): number {
  const { tiltX, tiltY } = w.map;
  if (Math.hypot(tiltX, tiltY) < 1e-6) return randInt(seededStream(w.cfg.seed, 7), RIM.count);
  return sectorIndex(SAUCER, datan2(tiltY, tiltX));
}

function wedgeMark(k: number, out: Vec2): Vec2 {
  out.x = dcos(k * WEDGE_STEP) * RIM.markRadius;
  out.y = dsin(k * WEDGE_STEP) * RIM.markRadius;
  return out;
}

const mark: Vec2 = { x: 0, y: 0 };
let fallingWedge = -1;

function onFallingWedge(x: number, y: number): boolean {
  return x * x + y * y > RIM.inner * RIM.inner && sectorIndex(SAUCER, datan2(y, x)) === fallingWedge;
}

function stepRim(w: World, t: Tempo): void {
  if (w.tick < t.sdStart) return;
  const m = w.map;
  if (m.sdCursor === 0 && w.tick === t.sdStart) wedgeOrderFrom(lowWedge(w), m.wedgeOrder);
  // Warnings start on schedule; `sdCursor` counts the wedges that have started.
  while (m.sdCursor < RIM.count && w.tick >= wedgeWarnTick(m.sdCursor, t)) {
    const k = m.wedgeOrder[m.sdCursor];
    m.wedgeWarn |= 1 << k;
    wedgeMark(k, mark);
    emit(w, EV.WEDGE, NO_SLOT, 0, k, 0, mark.x, mark.y);
    emitWarn(w, HazardKind.WEDGE, k, wedgeFallTick(m.sdCursor, t) - w.tick, mark.x, mark.y);
    m.sdCursor++;
  }
  for (let i = 0; i < m.sdCursor; i++) {
    const k = m.wedgeOrder[i];
    const bit = 1 << k;
    if ((m.wedgeFallen & bit) !== 0 || w.tick < wedgeFallTick(i, t)) continue;
    m.wedgeWarn &= ~bit;
    m.wedgeFallen |= bit;
    wedgeMark(k, mark);
    emit(w, EV.WEDGE, NO_SLOT, 1, k, 0, mark.x, mark.y);
    emitFire(w, HazardKind.WEDGE, k, mark.x, mark.y);
    fallingWedge = k;
    dropFromVanishedGround(w, TEACUP, onFallingWedge);
  }
}

// ---- Pose, danger -------------------------------------------------------------------------------

function writePose(w: World, tick: number, out: HazardPose): HazardPose {
  neutralHazardPose(out);
  const t = teacupTempo(w);
  out.cupAngle = cupAngle(tick, t);
  if (tick < t.sdStart || w.tick < t.sdStart) return out;
  const order = w.map.wedgeOrder;
  const crack = out.wedgeCrack;
  const fell = out.wedgeFallSec;
  for (let i = 0; i < RIM.count; i++) {
    const warn = wedgeWarnTick(i, t);
    if (tick < warn) break;
    const k = order[i];
    const fall = wedgeFallTick(i, t);
    if (crack) crack[k] = progress(tick, warn, fall);
    if (tick >= fall) {
      if (fell) fell[k] = (tick - fall) / TICK_HZ;
    } else {
      wedgeMark(k, mark);
      addTelegraph(out, HazardKind.WEDGE, k, warn, fall, mark.x, mark.y);
    }
  }
  return out;
}

function wedgeDanger(w: World, x: number, y: number, aheadTicks: number): number {
  const t = teacupTempo(w);
  if (w.tick < t.sdStart) return 0;
  const r2 = x * x + y * y;
  const pad = TUNE.bean.radius;
  if (r2 < (RIM.inner - pad) ** 2 || r2 > R * R) return 0;
  const k = sectorIndex(SAUCER, datan2(y, x));
  const m = w.map;
  if ((m.wedgeFallen & (1 << k)) !== 0) return 0;
  for (let i = 0; i < RIM.count; i++) {
    if (m.wedgeOrder[i] !== k) continue;
    const left = wedgeFallTick(i, t) - w.tick;
    const span = aheadTicks + RIM.warnBars * sdBarTicks(t);
    if (left > span) return 0;
    const lo = TUNING.danger.wedgeMin;
    return lo + (1 - lo) * (1 - Math.max(0, left) / span);
  }
  return 0;
}

export const TEACUP: MapDef = {
  id: MapId.TEACUP,
  name: 'Tipsy Teacup',
  tpb: MAP_TEMPO[MapId.TEACUP].tpb,
  tpbSD: MAP_TEMPO[MapId.TEACUP].tpbSD,
  bounds: { minX: -R, maxX: R, minY: -R, maxY: R },
  spawns: ringPoints(TUNING.spawnRadius, TUNING.spawnStartDeg, 8),
  voidKind: 'tea',
  center: { x: 0, y: 0 },
  initState(w) {
    CUBE_HOMES.forEach((p, i) => {
      spawnEntity(w, EK.CUBE, NO_SLOT, p.x, p.y, 0).data = i;
    });
  },
  groundAt(w, x, y) {
    return inSectoredDisc(x, y, SAUCER, w.map.wedgeFallen) ? 0 : VOID;
  },
  nearestGround(w, x, y, out) {
    return nearestOnSectoredDisc(x, y, SAUCER, w.map.wedgeFallen, out);
  },
  solids(_w, out) {
    return copySolids(SOLIDS, out);
  },
  onSolidContact() {},
  step(w) {
    const t = teacupTempo(w);
    stepTilt(w);
    stepCup(w, t);
    stepRim(w, t);
  },
  slideAccel(w, _x, _y, out) {
    const a = (rampOn(w) ? TUNING.tilt.slideRamp : TUNING.tilt.slideCalm) / maxTilt(w);
    out.x = w.map.tiltX * a;
    out.y = w.map.tiltY * a;
  },
  danger(w, x, y, aheadTicks) {
    return wedgeDanger(w, x, y, aheadTicks);
  },
  hazardPose(w, tick, out) {
    return writePose(w, tick, out);
  },
  crateExclusion(w, x, y) {
    if (nearSolid(SOLIDS, x, y, TUNE.crate.radius)) return true;
    const t = teacupTempo(w);
    const lead = RIM.crateLeadBars * TUNE.clock.beatsPerBar * t.tpb;
    return w.tick >= t.sdStart - lead && x * x + y * y > (RIM.inner - TUNE.crate.radius) ** 2;
  },
};
